import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  deleteToken,
  onMessage,
  isSupported,
  type Messaging,
} from 'firebase/messaging';
import { profileApi } from '@/services/api/profile';
import { queryClient } from '@/services/queryClient';
import { firebaseConfig, vapidKey, isFirebaseConfigured } from './firebaseConfig';

/**
 * Web push (FCM) glue for the browser.
 *
 * Best-effort and fully guarded: if the browser doesn't support push, the user
 * denies permission, or Firebase env vars aren't set yet, every function logs
 * and no-ops — the in-app Notification Centre (polling) keeps working. See
 * PUSH_SETUP.md for the env + service-worker steps that activate it.
 */

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let lastToken: string | null = null;
let unsubscribeOnMessage: (() => void) | null = null;

async function ensureMessaging(): Promise<Messaging | null> {
  if (messaging) return messaging;
  if (!isFirebaseConfigured()) {
    console.info('[push] Firebase web env not set; web push disabled.');
    return null;
  }
  if (!(await isSupported().catch(() => false))) {
    console.info('[push] This browser does not support FCM web push.');
    return null;
  }
  try {
    app = getApps()[0] ?? initializeApp(firebaseConfig as Record<string, string>);
    messaging = getMessaging(app);
    return messaging;
  } catch (err) {
    console.warn('[push] Firebase init failed (web push disabled):', err);
    return null;
  }
}

/**
 * Call after login. Requests notification permission, registers the FCM
 * service worker, obtains a token, and registers it with the backend. Wires the
 * foreground message handler. Idempotent.
 */
export async function registerWebPush(): Promise<void> {
  try {
    const m = await ensureMessaging();
    if (!m) return;

    if (!('serviceWorker' in navigator) || !('Notification' in window)) return;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.info('[push] notification permission not granted; skipping.');
      return;
    }

    const swRegistration = await navigator.serviceWorker.register(
      '/firebase-messaging-sw.js',
    );

    const token = await getToken(m, {
      vapidKey,
      serviceWorkerRegistration: swRegistration,
    });
    if (token && token !== lastToken) {
      await profileApi.registerPushToken(token);
      lastToken = token;
    }

    // Foreground messages aren't shown by the SW — refresh the in-app feed and
    // surface a lightweight browser notification.
    unsubscribeOnMessage?.();
    unsubscribeOnMessage = onMessage(m, (payload) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      const referralId = payload.data?.referralId;
      if (referralId) {
        queryClient.invalidateQueries({ queryKey: ['referral', referralId] });
      }
      // Web messages are data-only (title/body in data); fall back to the
      // notification field just in case. Show a foreground banner with the
      // real content (not the browser's generic default).
      const d = payload.data ?? {};
      const title = d.title ?? payload.notification?.title;
      const body = d.body ?? payload.notification?.body;
      if (title && Notification.permission === 'granted') {
        const n = new Notification(title, { body, data: d });
        n.onclick = () => {
          if (d.url) window.open(d.url, '_blank');
        };
      }
    });
  } catch (err) {
    console.warn('[push] web push registration failed (in-app feed unaffected):', err);
  }
}

/**
 * Local push teardown for logout. Returns the token that was registered so the
 * caller can pass it to the logout endpoint (the backend removes it from the
 * DB). Also deletes the local token so the next account gets a fresh one.
 */
export async function unregisterWebPush(): Promise<string | null> {
  let token = lastToken;
  try {
    unsubscribeOnMessage?.();
    unsubscribeOnMessage = null;

    // If the cached token was lost (e.g. after a page reload), fetch the current
    // one so logout can still remove the right DB row.
    if (!token) {
      const m = await ensureMessaging();
      if (m) {
        try {
          token = await getToken(m, { vapidKey });
        } catch {
          token = null;
        }
      }
    }

    if (messaging) {
      await deleteToken(messaging).catch(() => undefined);
    }
  } catch (err) {
    console.warn('[push] web push unregister failed (non-fatal):', err);
  } finally {
    lastToken = null;
  }
  return token;
}
